import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import once from 'lodash/once';
import { getCurrencyCode, getMeta } from 'modules/meta/selectors';
import { getIsRtlLanguage } from 'modules/meta/selectors';
import { imageProvider } from 'utils/imageProvider';
import { v4 } from 'uuid';
import { SmoothScrollNavigation } from '@daedalus/atlas/experimental/SmoothScrollNavigation';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity, HotelContext, TrackEventPayload } from '@daedalus/core/src/analytics/types/Events';
import { getHotelPrices } from '@daedalus/core/src/carouselWidgets/utils';
import { SearchApiError } from '@daedalus/core/src/sapi/services/searchApi';
import { SuggestionHotelCard, SuggestionHotelCardSkeleton } from '@daedalus/shared/src/search/SuggestionHotelCard';
import { SimilarHotel } from '../types';
import { useGetDealsForTonight } from './hooks/useGetDealsForTonight';
const _exp = /*#__PURE__*/() => Flex.Row;
const SkeletonContainer = /*#__PURE__*/styled(_exp())({
  name: "SkeletonContainer",
  class: "s1op4oe1",
  propsAsIs: true
});
interface HotelsListProps {
  hotelId?: string;
  placeId?: string;
  rooms: string;
  checkIn: string;
  checkOut: string;
  trackingPayload: Pick<TrackEventPayload, 'component' | 'payload' | 'team'>;
  onHotelClick: ({
    hotelId
  }) => void;
  hasInsideMargin?: boolean;
  isFetchingGeo?: boolean;
  onRequestStart?: () => void;
  onRequestComplete?: () => void;
  shouldWaitRequestComplete?: boolean;
  hasSidePaddings: boolean;
  numberOfResults?: number;
  selectedPlaceId: string;
}
interface HotelItemProps extends Pick<HotelsListProps, 'checkIn' | 'checkOut' | 'trackingPayload' | 'hasInsideMargin'> {
  similarHotel: SimilarHotel;
  position: number;
  onClick: ({
    hotelId
  }) => void;
}
const HotelItem = ({
  similarHotel,
  trackingPayload,
  hasInsideMargin,
  checkIn,
  checkOut,
  onClick,
  position
}: HotelItemProps) => {
  const {
    includeTaxes,
    includeLocalTaxes
  } = useSelector(getMeta);
  const currencyCode = useSelector(getCurrencyCode);
  const dispatch = useDispatch();
  if (!similarHotel || !similarHotel?.hotelEntity) {
    return null;
  }
  const {
    hotelEntity,
    offerEntity
  } = similarHotel;
  const {
    hotelName,
    reviewCount,
    starRating,
    imageURIs,
    guestRating,
    objectID
  } = hotelEntity;
  const {
    price,
    anchorPrice
  } = getHotelPrices({
    offerEntity,
    checkIn,
    checkOut,
    includeTaxes,
    includeLocalTaxes
  });
  const onHotelClick = () => {
    const hotelContext = {
      hotelId: objectID,
      hotelPosition: position
    } as HotelContext;
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.HotelCard,
      action: Action.Clicked,
      analyticsContext: {
        [AnalyticsContext.HotelContext]: hotelContext
      },
      ...trackingPayload
    }));
    onClick({
      hotelId: objectID
    });
  };
  return <SuggestionHotelCard key={objectID} hotelName={hotelName} starRating={starRating} reviewRating={guestRating?.overall} reviewRatingCount={reviewCount} imageUrl={imageURIs?.[0]} currency={currencyCode} price={price} anchorPrice={anchorPrice} onHotelClick={onHotelClick} imageProvider={imageProvider} hasInsideMargin={hasInsideMargin} />;
};
const SuggestedHotelsSkeleton = () => <SkeletonContainer>
    {[...Array(5)].map((_, index) => <SuggestionHotelCardSkeleton key={index} />)}
  </SkeletonContainer>;
export const HotelsList = ({
  hotelId,
  placeId,
  rooms,
  checkIn,
  checkOut,
  trackingPayload,
  onHotelClick,
  hasInsideMargin,
  hasSidePaddings,
  isFetchingGeo = false,
  onRequestStart,
  onRequestComplete,
  shouldWaitRequestComplete,
  numberOfResults = 10,
  selectedPlaceId
}: HotelsListProps) => {
  const instanceId = useRef(v4());
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const dispatch = useDispatch();
  const [requestComplete, setRequestComplete] = useState(false);
  const handleRequestStart = id => {
    if (id !== instanceId.current) return;
    setRequestComplete(false);
    onRequestStart?.();
  };
  const handleRequestComplete = id => {
    if (id !== instanceId.current) return;
    setRequestComplete(true);
    onRequestComplete?.();
  };
  const handleRequestError = (id: string, error: Error) => {
    if (id !== instanceId.current) return;
    dispatch(trackEvent({
      category: Category.System,
      entity: Entity.Search,
      action: Action.Errored,
      payload: {
        sourceComponent: 'HotelsList'
      },
      analyticsContext: {
        [AnalyticsContext.ErrorContext]: {
          errorType: 'GetDealsFailed',
          errorMessage: (error as Error).message
        }
      }
    }));
    if (typeof Sentry !== 'undefined') Sentry.captureException(new SearchApiError('SuggestedHotels: Error fetching offers', error), {
      extra: trackingPayload
    });
  };
  const {
    hotelIds,
    hotels,
    hotelsReceived
  } = useGetDealsForTonight({
    placeId,
    hotelId,
    rooms,
    checkIn,
    checkOut,
    instanceId: instanceId.current,
    onRequestStart: handleRequestStart,
    onRequestComplete: handleRequestComplete,
    onRequestError: handleRequestError,
    numberOfResults
  });
  const {
    current: trackSimilarHotelsScrollOnce
  } = useRef(once(() => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.Carousel,
      action: Action.Scrolled,
      ...trackingPayload
    }));
  }));
  if (shouldWaitRequestComplete && !requestComplete || !hotelsReceived || isFetchingGeo) {
    return selectedPlaceId === placeId ? <SuggestedHotelsSkeleton /> : null;
  }
  return selectedPlaceId === placeId ? <SmoothScrollNavigation isRtlLanguage={isRtlLanguage} onScroll={trackSimilarHotelsScrollOnce} hasSidePaddings={hasSidePaddings}>
      {hotelIds?.map((hotelId, index) => <HotelItem key={hotelId} similarHotel={hotels?.[hotelId]} trackingPayload={{
      ...trackingPayload,
      payload: {
        totalNumber: numberOfResults,
        index: index + 1,
        searchItem: {
          hotelId,
          checkIn,
          checkOut,
          rooms
        }
      }
    }} checkIn={checkIn} checkOut={checkOut} position={index + 1} hasInsideMargin={hasInsideMargin} onClick={onHotelClick} />)}
    </SmoothScrollNavigation> : null;
};

require("./HotelsList.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./HotelsList.tsx");